import React, { createContext, useState, ReactNode, useMemo, useCallback, useEffect, type JSX } from 'react';

import { AddresCheckAlert } from '@/components/Modals/AddressCheckAlert/AddresCheckAlert';
import { BirthdayUpdateAlert } from '@/components/Modals/BirthdayUpdateAlert/BirthdayUpdateAler';
import { DocumentAddCheckAlert } from '@/components/Modals/DocumentAddCheckAlert/DocumentAddCheckAlert';
import { DocumentDataMissingAlert } from '@/components/Modals/DocumentDataMissingAlert';
import { ExternalServiceDetailsAlert } from '@/components/Modals/ExternalServiceDetailsAlert';
import { InsuranceOrderRenewalAlertAlert } from '@/components/Modals/InsuranceOrderRenewalAlert/InsuranceOrderRenewalAlert';
import { InsurancePhoneChangeAlert } from '@/components/Modals/InsurancePhoneChangeAlert';
import { LeavingWhileInProgressAlert } from '@/components/Modals/LeavingWhileInProgressAlert.tsx';
import { PaymentDetailsAlert } from '@/components/Modals/PaymentDetailsAlert';
import { PayOrLeaveAlert } from '@/components/Modals/PayOrLeaveAlert';
import { ServerErrorAlert } from '@/components/Modals/ServerErrorAlert/ServerErrorAlert';
import { ServiceDraftExistingAlert } from '@/components/Modals/ServiceDraftExistingAlert';
import { SocialProtectAppealInfoAlert } from '@/components/Modals/SocialProtectAppealInfoAlert';

import { on, die } from 'src/eventBus';

import {
  AddressCheckAlertRender,
  DocumentMissingAlertRender,
  ExternalServiceDetailsAlertRender,
  InsuranceOrderRenewalAlertRender,
  InsurancePhoneChangeAlertRender,
  LeavingWhileInProgressAlertRender,
  ModalName,
  PayOrLeaveAlertRender,
  PaymentDetailsAlertRender,
  RenderModalProps,
  ServiceDraftExistingAlertRender,
  SocialProtectAppealInfoAlertRender,
  BirthdayAlertRender,
} from './modalTypes';

export interface ModalContextInterface {
  isModalOpen: boolean;
  toggleModal: (modalStatus?: boolean) => void;
  renderSpecificModal: (props: RenderModalProps) => void;
}

const ModalContext = createContext<ModalContextInterface>({
  isModalOpen: false,
  toggleModal: () => null,
  renderSpecificModal: () => null,
});

interface Props {
  children: ReactNode;
}

export const ModalContextProvider = ({ children }: Props): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalNameToRender, setModalNameToRender] = useState<Nullable<RenderModalProps['modalName']>>(null);
  const [modalPropsToRender, setModalPropsToRender] = useState<Nullable<RenderModalProps['modalProps']>>(null);
  const handleCloseEvent = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  useEffect(() => {
    on('modal_force_close', handleCloseEvent);

    return (): void => {
      die('modal_force_close');
    };
  }, [handleCloseEvent]);

  const modalToRender = useMemo(() => {
    if (!isModalOpen) return <React.Fragment />;

    switch (modalNameToRender) {
      case ModalName.BirthdayAlert:
        return <BirthdayUpdateAlert {...(modalPropsToRender as BirthdayAlertRender['modalProps'])} />;

      case ModalName.AddressCheckAlert:
        return <AddresCheckAlert {...(modalPropsToRender as AddressCheckAlertRender['modalProps'])} />;

      case ModalName.DocumentMissingAlert:
        return <DocumentDataMissingAlert {...(modalPropsToRender as DocumentMissingAlertRender['modalProps'])} />;

      case ModalName.DocumentAddCheckAlert:
        return <DocumentAddCheckAlert />;

      case ModalName.ServiceDraftExistingAlert:
        return <ServiceDraftExistingAlert {...(modalPropsToRender as ServiceDraftExistingAlertRender['modalProps'])} />;

      case ModalName.ServerErrorAlert:
        return <ServerErrorAlert />;

      case ModalName.PayOrLeaveAlert:
        return <PayOrLeaveAlert {...(modalPropsToRender as PayOrLeaveAlertRender['modalProps'])} />;

      case ModalName.PaymentDetailsAlert:
        return <PaymentDetailsAlert {...(modalPropsToRender as PaymentDetailsAlertRender['modalProps'])} />;

      case ModalName.LeavingWhileInProgressAlert:
        return (
          <LeavingWhileInProgressAlert {...(modalPropsToRender as LeavingWhileInProgressAlertRender['modalProps'])} />
        );

      case ModalName.ExternalServiceDetailsAlert:
        return (
          <ExternalServiceDetailsAlert {...(modalPropsToRender as ExternalServiceDetailsAlertRender['modalProps'])} />
        );

      case ModalName.InsurancePhoneChangeAlert:
        return <InsurancePhoneChangeAlert {...(modalPropsToRender as InsurancePhoneChangeAlertRender['modalProps'])} />;

      case ModalName.InsuranceOrderRenewalAlert:
        return (
          <InsuranceOrderRenewalAlertAlert
            {...(modalPropsToRender as InsuranceOrderRenewalAlertRender['modalProps'])}
          />
        );

      case ModalName.SocialProtectAppealInfoAlert:
        return (
          <SocialProtectAppealInfoAlert {...(modalPropsToRender as SocialProtectAppealInfoAlertRender['modalProps'])} />
        );

      default:
        return <React.Fragment />;
    }
  }, [modalNameToRender, modalPropsToRender, isModalOpen]);

  const toggleModal = useCallback((value = !isModalOpen) => setIsModalOpen(value), [isModalOpen]);

  const renderSpecificModal = useCallback(({ modalName, modalProps }: RenderModalProps) => {
    setModalNameToRender(modalName);
    setModalPropsToRender(modalProps);
    setIsModalOpen(true);
  }, []);

  return (
    <ModalContext.Provider
      value={{
        isModalOpen,
        toggleModal,
        renderSpecificModal,
      }}
    >
      {modalToRender}
      {children}
    </ModalContext.Provider>
  );
};

export default ModalContext;
